'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'averiane_certificate_recipient';

type RichBlocksContextValue = {
  quizPassed: boolean;
  hasQuiz: boolean;
  recipientName: string;
  recipientEmail: string;
  setQuizPassed: (passed: boolean) => void;
  setHasQuiz: (has: boolean) => void;
  setRecipientName: (name: string) => void;
  setRecipientEmail: (email: string) => void;
};

const RichBlocksContext = createContext<RichBlocksContextValue | null>(null);

export function RichBlocksProvider({
  children,
  hasQuiz: initialHasQuiz = false,
}: {
  children: ReactNode;
  hasQuiz?: boolean;
}) {
  const [quizPassed, setQuizPassed] = useState(false);
  const [hasQuiz, setHasQuiz] = useState(initialHasQuiz);
  const [recipientName, setRecipientNameState] = useState('');
  const [recipientEmail, setRecipientEmailState] = useState('');

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.name) setRecipientNameState(parsed.name);
        if (parsed?.email) setRecipientEmailState(parsed.email);
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  const persistRecipient = useCallback((name: string, email: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ name, email }));
    } catch {
      // ignore storage errors
    }
  }, []);

  const setRecipientName = useCallback(
    (name: string) => {
      setRecipientNameState(name);
      persistRecipient(name, recipientEmail);
    },
    [recipientEmail, persistRecipient]
  );

  const setRecipientEmail = useCallback(
    (email: string) => {
      setRecipientEmailState(email);
      persistRecipient(recipientName, email);
    },
    [recipientName, persistRecipient]
  );

  return (
    <RichBlocksContext.Provider
      value={{
        quizPassed,
        hasQuiz,
        recipientName,
        recipientEmail,
        setQuizPassed,
        setHasQuiz,
        setRecipientName,
        setRecipientEmail,
      }}
    >
      {children}
    </RichBlocksContext.Provider>
  );
}

export function useRichBlocks() {
  const ctx = useContext(RichBlocksContext);
  if (!ctx) {
    throw new Error('useRichBlocks must be used inside RichBlocksProvider');
  }
  return ctx;
}
